import React, { useState, useEffect } from 'react';
import { Button, Typography, Box, Tab, Tabs, Paper } from "@mui/material";
import { Add, Edit } from "@mui/icons-material";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import FormDrawer from "components/Shared/FormDrawer";
import AppTable from "components/Shared/AppTable";
import { useTripDetailContext } from "contexts/TripDetailContext";
import { useTablePagination } from "hooks";

import {
  useAddTripLoadingMutation,
  useLazyListTripLoadingsQuery,
  useUpdateTripLoadingMutation,
} from "store/services/tripApi";

import TripLoadingForm from "./TripLoadingForm";

const TripLoadingTab = () => {
  const { id: tripId } = useParams();
  const [searchParams] = useSearchParams();

  const [showForm, setShowForm] = useState(false);
  const [selectedTripLoading, setSelectedTripLoading] = useState(null);
  const [activeStatusTab, setActiveStatusTab] = useState("Active"); // Local state for Active/Inactive tabs

  const { tripDetail, loadingTrip } = useTripDetailContext();
  const { currentPage, perPage, handlePageChange, handlePerRowsChange } = useTablePagination();

  const keyword = searchParams.get("search") || "";

  const [fetchTripLoadings, { data: tripLoadingsData, isLoading, isFetching, error }] =
    useLazyListTripLoadingsQuery();

  const [addTripLoading] = useAddTripLoadingMutation();
  const [updateTripLoading] = useUpdateTripLoadingMutation();

  useEffect(() => {
    if (!tripId) return;
    fetchTripLoadings({
      tripId,
      page: currentPage,
      limit: perPage,
      active: activeStatusTab === "Active",
      ...(keyword && { keyword }),
    });
  }, [tripId, currentPage, perPage, activeStatusTab, keyword, fetchTripLoadings]);

  const handleStatusTabChange = (event, newValue) => {
    setActiveStatusTab(newValue);
  };

  const handleSubmit = async (formData) => {
    try {
      if (selectedTripLoading) {
        await updateTripLoading({
          tripId,
          loadingId: selectedTripLoading._id,
          ...formData,
        }).unwrap();
        toast.success("Trip loading updated successfully");
      } else {
        await addTripLoading({ tripId, ...formData }).unwrap();
        toast.success("Trip loading added successfully");
      }
      setShowForm(false);
      setSelectedTripLoading(null);
    } catch (err) {
      toast.error(err?.data?.message ?? "An error occurred");
    }
  };

  const columns = [
    {
      name: "Loading Branch",
      selector: (row) => row.loadingBranch?.name || "N/A",
      sortable: true,
    },
    {
      name: "Unloading Branch",
      selector: (row) => row.unLoadingBranch?.name || "N/A",
      sortable: true,
    },
    {
      name: "Loading ID",
      selector: (row) => row.loadingId?.loadingId || "N/A",
      sortable: true,
    },
    {
      name: "Actions",
      cell: (row) => (
        <Button
          onClick={() => {
            setSelectedTripLoading(row);
            setShowForm(true);
          }}
        >
          <Edit fontSize="small" />
        </Button>
      ),
    },
  ];

  if (loadingTrip || !tripDetail) {
    return <div>Loading trip details...</div>;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginRight: "10px", mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => {
            setSelectedTripLoading(null);
            setShowForm(true);
          }}
          disabled={loadingTrip}
        >
          Add Trip Loading
        </Button>
      </Box>

      {/* Custom Status Tabs using Material-UI Tabs directly */}
      <Paper elevation={0} sx={{ mb: 2 }}>
        <Tabs
          value={activeStatusTab}
          onChange={handleStatusTabChange}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Active" value="Active" />
          <Tab label="Inactive" value="Inactive" />
        </Tabs>
      </Paper>

      <AppTable
        columns={columns}
        data={tripLoadingsData?.tripLoadings || []}
        progressPending={isLoading || isFetching}
        paginationServer
        paginationPerPage={perPage}
        paginationTotalRows={tripLoadingsData?.totalItems || 0}
        paginationDefaultPage={currentPage}
        onChangePage={handlePageChange}
        onChangeRowsPerPage={handlePerRowsChange}
        noDataComponent={
          <Typography py={3} textAlign="center" color="textSecondary">
            No trip loadings found
          </Typography>
        }
      />

      <FormDrawer
        open={showForm}
        onClose={() => {
          setShowForm(false);
          setSelectedTripLoading(null);
        }}
        title={`${selectedTripLoading ? "Edit" : "Add"} Trip Loading`}
      >
        <TripLoadingForm
          trip={tripDetail}
          tripLoading={selectedTripLoading}
          onSubmit={handleSubmit}
          onCancel={() => {
            setShowForm(false);
            setSelectedTripLoading(null);
          }}
          isBusy={loadingTrip}
        />
      </FormDrawer>
    </Box>
  );
};

export default TripLoadingTab;